import { useState, useMemo, useEffect, type ReactNode } from "react";
import clsx from "clsx";
import { Link, useParams } from "react-router-dom";
import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { api } from "../lib/api";
import { useAuth } from "../auth/AuthProvider";
import type {
  ClientDetailResponse,
  CampaignDetail,
  AdsetDetail,
  AdDetail,
  SyncResult,
  Annotation,
  AnnotationType,
  Delta,
  Diagnostic,
  LifecycleState,
} from "../lib/types";
import { DIAGNOSTIC_META, buildDiagnosticTooltip } from "../lib/diagnostics";
import { LIFECYCLE_META, buildLifecycleTooltip } from "../lib/lifecycle";
import { Badge } from "../components/Badge";
import { SegmentedControl } from "../components/SegmentedControl";
import { COLORS } from "../lib/theme";
import { money, num, roasX, longDate, shortDate, dateTime, conversionLabel } from "../lib/format";
import { CONVERSION_TYPES } from "../lib/constants";
import {
  ANNOTATION_TYPE_META,
  ANNOTATION_TYPES,
  markersByDate,
  isUnannotatedAuto,
} from "../lib/annotations";
import {
  PageHeader,
  Loading,
  ErrorNote,
  EmptyState,
  Spinner,
  Card,
  Modal,
  TableScroll,
} from "../components/ui";
import { RangePicker } from "../components/RangePicker";
import { StatTile } from "../components/StatTile";
import { DeltaBadge } from "../components/DeltaBadge";
import { FlagBadge } from "../components/FlagBadge";
import { MetricLineChart } from "../components/MetricLineChart";
import { IconRefresh } from "../components/icons";

export function ClientDetail() {
  const { id } = useParams<{ id: string }>();
  const { me } = useAuth();
  const isAdmin = me?.role === "admin";
  const [days, setDays] = useState(7);
  const [markerDate, setMarkerDate] = useState<string | null>(null);
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["clientDetail", id, days],
    queryFn: () => api.get<ClientDetailResponse>(`/api/clients/${id}/detail?days=${days}`),
    enabled: Boolean(id),
  });

  const refresh = useMutation({
    mutationFn: () => api.post<SyncResult>(`/api/clients/${id}/refresh`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["clientDetail", id] }),
  });

  if (isLoading) return <Loading />;
  if (error) return <ErrorNote message={(error as Error).message} />;
  if (!data) return <EmptyState title="Client not found" />;

  const { client, totals, window, campaigns, dailySeries, revenueEntries } = data;
  const convType = campaigns[0]?.conversionType ?? "initiate_checkout";
  const cur = totals.spendCurrency ?? client.spend_currency ?? "MUR";

  return (
    <div>
      <PageHeader
        title={client.name}
        subtitle={
          <>
            {isAdmin && (
              <Link to="/" className="text-brand hover:underline">
                ← Overview
              </Link>
            )}{" "}
            <span className="text-muted">
              {longDate(window.currentStart)} – {longDate(window.currentEnd)} vs prior {window.days}d
            </span>
          </>
        }
        actions={
          <>
            <RangePicker days={days} onChange={setDays} />
            {client.meta_account_id && (
              <div className="flex items-center gap-2">
                {data.lastSyncedAt && (
                  <span className="text-xs text-muted" title={longDate(data.lastSyncedAt)}>
                    Last synced {dateTime(data.lastSyncedAt)}
                  </span>
                )}
                {isAdmin && (
                  <button
                    className="btn-ghost"
                    onClick={() => refresh.mutate()}
                    disabled={refresh.isPending}
                  >
                    {refresh.isPending ? (
                      <Spinner className="h-4 w-4" />
                    ) : (
                      <IconRefresh width={16} height={16} />
                    )}
                    Refresh
                  </button>
                )}
              </div>
            )}
          </>
        }
      />

      {/* Stat tiles */}
      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile label="Spend" value={money(totals.current.spend, cur)} delta={totals.deltas.spend} deltaGood="neutral" />
        <StatTile
          label="Conversions"
          value={num(totals.current.conversions)}
          delta={totals.deltas.conversions}
          deltaGood="up"
          sub={conversionLabel(convType)}
        />
        <StatTile
          label="Cost / conversion"
          value={totals.current.costPerConversion == null ? "—" : money(totals.current.costPerConversion, cur, 2)}
          delta={totals.deltas.costPerConversion}
          deltaGood="down"
        />
        <StatTile
          label="ROAS"
          value={
            totals.roas != null && totals.roasEstimated ? (
              <span className="inline-flex items-baseline gap-1">
                {roasX(totals.roas)}
                <span className="text-xs font-normal text-warning">est.</span>
              </span>
            ) : (
              roasX(totals.roas)
            )
          }
          sub={
            totals.revenueLogged ? (
              <>
                {money(totals.revenueCurrent)} revenue ÷ {money(totals.spendMur)} spend (MUR)
                {totals.roasEstimated && (
                  <span className="mt-0.5 block text-warning">
                    est. — prorated from a monthly/weekly figure; log daily revenue for exact ROAS
                  </span>
                )}
                {totals.margin != null && (
                  <span
                    className={clsx(
                      "mt-0.5 block",
                      totals.profitRoas != null && totals.profitRoas < 1 && "text-critical"
                    )}
                  >
                    Profit ROAS {roasX(totals.profitRoas)} · {Math.round(totals.margin * 100)}% margin
                    {totals.profitRoas != null && totals.profitRoas < 1 && " — underwater"}
                  </span>
                )}
              </>
            ) : (
              "revenue not logged for this period"
            )
          }
        />
      </div>

      {/* Charts */}
      {client.meta_account_id ? (
        <div className="mb-6">
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
            <MetricLineChart
              title="Cost per conversion"
              data={dailySeries}
              dataKey="costPerConversion"
              color={COLORS.series1}
              format={(v) => money(v, cur, 0)}
              seriesLabel="Cost / conv"
              annotations={data.annotations}
              onMarkerClick={setMarkerDate}
            />
            <MetricLineChart
              title={conversionLabel(convType)}
              data={dailySeries}
              dataKey="conversions"
              color={COLORS.series2}
              format={(v) => num(v, 0)}
              seriesLabel="Conversions"
              annotations={data.annotations}
              onMarkerClick={setMarkerDate}
            />
            <MetricLineChart
              title="ROAS"
              data={dailySeries}
              dataKey="roas"
              color={COLORS.series3}
              format={(v) => roasX(v)}
              seriesLabel="ROAS"
              secondary={
                totals.margin != null
                  ? { dataKey: "profitRoas", color: COLORS.series1, seriesLabel: "Profit ROAS" }
                  : undefined
              }
              referenceY={totals.margin != null ? 1 : undefined}
              annotations={data.annotations}
              onMarkerClick={setMarkerDate}
            />
          </div>
          {totals.roasEstimated && (
            <p className="mt-2 text-xs text-warning">
              ROAS trend is estimated — prorated from a monthly/weekly figure; log daily revenue
              for an exact trend.
            </p>
          )}
          {isAdmin && totals.margin == null && (
            <p className="mt-2 text-xs text-muted">
              Set a profit margin on this client (Clients → edit) to see Profit ROAS — whether ad
              spend is outrunning the profit it drives.
            </p>
          )}
          <AnnotationsSection
            clientId={id!}
            isAdmin={isAdmin}
            annotations={data.annotations}
            campaigns={campaigns}
            selectedDate={markerDate}
            onSelectDate={setMarkerDate}
            days={days}
          />
        </div>
      ) : (
        <Card className="mb-6 text-sm text-muted">
          This client has no Meta ad account connected — revenue-only tracking.
        </Card>
      )}

      {/* Campaign breakdown */}
      {campaigns.length > 0 && (
        <CampaignsSection
          campaigns={campaigns}
          isAdmin={isAdmin}
          clientId={id!}
          currency={cur}
          days={days}
        />
      )}

      {/* Revenue log */}
      <section>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-ink">Revenue (MUR)</h2>
          {isAdmin && (
            <Link to={`/revenue?client=${id}`} className="btn-ghost">
              Log revenue
            </Link>
          )}
        </div>
        {revenueEntries.length === 0 ? (
          <EmptyState
            title="No revenue logged yet"
            hint={
              isAdmin
                ? "Log store sales to compute true ROAS — Meta can't see store revenue without a purchase pixel."
                : "Your account manager will add revenue figures here."
            }
          />
        ) : (
          <div className="card overflow-hidden">
            <TableScroll>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-hairline text-left text-xs uppercase tracking-wide text-muted">
                  <th className="px-4 py-3 font-medium">Period</th>
                  <th className="px-4 py-3 font-medium">Type</th>
                  <th className="px-4 py-3 text-right font-medium">Amount</th>
                  <th className="px-4 py-3 font-medium">Note</th>
                </tr>
              </thead>
              <tbody>
                {revenueEntries.map((r) => (
                  <tr key={r.id} className="border-b border-hairline/60 last:border-0">
                    <td className="px-4 py-3 tnum">
                      {longDate(r.period_start)}
                      {r.period_end !== r.period_start && ` – ${longDate(r.period_end)}`}
                    </td>
                    <td className="px-4 py-3 capitalize text-ink-secondary">{r.period_type}</td>
                    <td className="px-4 py-3 text-right tnum font-medium">{money(r.amount_mur)}</td>
                    <td className="px-4 py-3 text-muted">{r.note ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </TableScroll>
          </div>
        )}
      </section>
    </div>
  );
}

const PAGE_SIZE = 5;
type StatusFilterValue = "all" | "active" | "inactive";

/** A campaign is "active" when its Meta effective status is ACTIVE. */
function isActiveStatus(status: string | null | undefined): boolean {
  return (status ?? "").toUpperCase() === "ACTIVE";
}

/**
 * Campaign breakdown: filter by status, active-first sort, 5-per-page paginate.
 * All client-side — every campaign is already in the detail response.
 */
function CampaignsSection({
  campaigns,
  isAdmin,
  clientId,
  currency,
  days,
}: {
  campaigns: CampaignDetail[];
  isAdmin: boolean;
  clientId: string;
  currency: string;
  days: number;
}) {
  const [statusFilter, setStatusFilter] = useState<StatusFilterValue>("all");
  const [page, setPage] = useState(0);

  // Filter by status, then active campaigns first, then by spend (desc).
  const rows = useMemo(() => {
    const filtered = campaigns.filter((c) => {
      if (statusFilter === "active") return isActiveStatus(c.status);
      if (statusFilter === "inactive") return !isActiveStatus(c.status);
      return true;
    });
    return filtered.slice().sort((a, b) => {
      const rank = (c: CampaignDetail) => (isActiveStatus(c.status) ? 0 : 1);
      const r = rank(a) - rank(b);
      return r !== 0 ? r : b.current.spend - a.current.spend;
    });
  }, [campaigns, statusFilter]);

  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const current = Math.min(page, pageCount - 1);
  const start = current * PAGE_SIZE;
  const visible = rows.slice(start, start + PAGE_SIZE);

  // Reset to the first page whenever the filter changes.
  useEffect(() => {
    setPage(0);
  }, [statusFilter]);

  const activeCount = campaigns.filter((c) => isActiveStatus(c.status)).length;

  return (
    <section className="mb-6">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-ink">
          Campaigns{" "}
          <span className="font-normal text-muted">
            ({activeCount} active / {campaigns.length})
          </span>
        </h2>
        <div className="flex items-center gap-2">
          <Link to="/docs" className="text-xs text-muted hover:text-ink">
            How diagnostics work
          </Link>
          <StatusFilter value={statusFilter} onChange={setStatusFilter} />
        </div>
      </div>
      {/* Mobile: stacked campaign cards (no sideways scroll for core metrics). */}
      <div className="space-y-2 md:hidden">
        {visible.map((c) => (
          <CampaignCard
            key={c.id}
            c={c}
            isAdmin={isAdmin}
            clientId={clientId}
            currency={currency}
            days={days}
          />
        ))}
        {visible.length === 0 && (
          <div className="card p-4 text-center text-sm text-muted">
            No {statusFilter === "all" ? "" : statusFilter} campaigns.
          </div>
        )}
      </div>

      {/* Desktop: table. */}
      <div className="card hidden overflow-hidden md:block">
        <TableScroll>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-hairline text-left text-xs uppercase tracking-wide text-muted">
                <th className="px-4 py-3 font-medium">Campaign</th>
                <th className="px-4 py-3 font-medium">Counting</th>
                <th className="px-4 py-3 text-right font-medium">Spend</th>
                <th className="px-4 py-3 text-right font-medium">Conversions</th>
                <th className="px-4 py-3 text-right font-medium">Cost / conv</th>
                <th className="px-4 py-3 text-right font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((c) => (
                <CampaignRow
                  key={c.id}
                  c={c}
                  isAdmin={isAdmin}
                  clientId={clientId}
                  currency={currency}
                  days={days}
                />
              ))}
              {visible.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-sm text-muted">
                    No {statusFilter === "all" ? "" : statusFilter} campaigns.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </TableScroll>
      </div>

      {rows.length > PAGE_SIZE && (
        <div className="mt-2 flex items-center justify-between rounded-lg border border-hairline bg-surface px-4 py-2 text-xs text-muted">
          <span className="tnum">
            Showing {start + 1}–{Math.min(start + PAGE_SIZE, rows.length)} of {rows.length}
          </span>
          <div className="flex items-center gap-1">
            <button
              className="btn-ghost px-2 py-1"
              disabled={current === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Prev
            </button>
            <span className="px-1 tnum">
              {current + 1}/{pageCount}
            </span>
            <button
              className="btn-ghost px-2 py-1"
              disabled={current >= pageCount - 1}
              onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function StatusFilter({
  value,
  onChange,
}: {
  value: StatusFilterValue;
  onChange: (v: StatusFilterValue) => void;
}) {
  return (
    <SegmentedControl
      ariaLabel="Filter campaigns by status"
      options={[
        { value: "all", label: "All" },
        { value: "active", label: "Active" },
        { value: "inactive", label: "Inactive" },
      ]}
      value={value}
      onChange={onChange}
    />
  );
}

function StatusBadge({ status }: { status: string | null }) {
  const active = isActiveStatus(status);
  return (
    <span
      title={status ?? "unknown"}
      className="inline-flex shrink-0 items-center gap-1 text-[10px] uppercase tracking-wide text-muted"
    >
      <span className={clsx("h-1.5 w-1.5 rounded-full", active ? "bg-good" : "bg-muted")} />
      {active ? "Active" : status ? status.toLowerCase() : "inactive"}
    </span>
  );
}

/** Shared per-campaign state + lazy ads/ad-sets queries (desktop row + mobile card). */
function useCampaignRow(c: CampaignDetail, clientId: string, days: number) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<"ads" | "adsets">("ads");
  const override = useMutation({
    mutationFn: (conversionType: string | null) =>
      api.patch(`/api/campaigns/${c.id}/conversion-type`, { conversionType }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["clientDetail", clientId] }),
  });
  const adsets = useQuery({
    queryKey: ["adsets", c.id, days],
    queryFn: () => api.get<{ adsets: AdsetDetail[] }>(`/api/campaigns/${c.id}/adsets?days=${days}`),
    enabled: open && view === "adsets",
  });
  const ads = useQuery({
    queryKey: ["ads", c.id, days],
    queryFn: () => api.get<{ ads: AdDetail[] }>(`/api/campaigns/${c.id}/ads?days=${days}`),
    enabled: open && view === "ads",
  });
  return { open, setOpen, view, setView, override, ads, adsets };
}

/** Creative-fatigue / high-frequency chips shown next to a campaign's flag. */
function CampaignFlagChips({ c, days }: { c: CampaignDetail; days: number }) {
  if (!c.flags.creativeFatigue && !c.flags.highFrequency) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {c.flags.creativeFatigue && (
        <Badge
          tone="critical"
          title={
            c.flags.maxCreativeAgeDays != null
              ? `Creative live ${c.flags.maxCreativeAgeDays} days`
              : undefined
          }
        >
          Creative Fatigue
        </Badge>
      )}
      {c.flags.highFrequency && (
        <Badge tone="critical" title={`Frequency ${c.flags.frequency ?? "?"} over ${days} days`}>
          High Freq {c.flags.frequency ?? ""} · {days}d
        </Badge>
      )}
    </div>
  );
}

/**
 * Deterministic funnel-diagnostic pills (amber). Only issues get a pill; the
 * hover title traces the exact signals that fired it for this campaign.
 */
function DiagnosticPills({
  diagnostics,
  currency,
}: {
  diagnostics: Diagnostic[];
  currency: string;
}) {
  if (!diagnostics.length) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {diagnostics.map((d) => (
        <Badge key={d.kind} tone="warning" interactive title={buildDiagnosticTooltip(d, currency)}>
          {DIAGNOSTIC_META[d.kind].label}
        </Badge>
      ))}
    </div>
  );
}

/**
 * Lifecycle context pills (blue) — New / Modified / Ending soon. Neutral context,
 * not alerts: they explain WHY the numbers read how they do (grow vs. expected dip).
 */
function LifecyclePills({ lifecycle }: { lifecycle: LifecycleState[] }) {
  if (!lifecycle.length) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {lifecycle.map((s) => (
        <Badge key={s} tone="info" interactive title={buildLifecycleTooltip(s)}>
          {LIFECYCLE_META[s].label}
        </Badge>
      ))}
    </div>
  );
}

/** One labelled metric (value + delta) inside a mobile campaign card. */
function MiniMetric({
  label,
  value,
  delta,
  good,
}: {
  label: string;
  value: ReactNode;
  delta: Delta;
  good: "up" | "down" | "neutral";
}) {
  return (
    <div className="rounded-lg bg-surface-2 px-2 py-1.5">
      <div className="text-[10px] uppercase tracking-wide text-muted">{label}</div>
      <div className="mt-0.5 tnum text-sm font-medium text-ink">{value}</div>
      <DeltaBadge delta={delta} good={good} />
    </div>
  );
}

/** Expanded ads / ad-sets detail — shared by the desktop row and the mobile card. */
function CampaignDetailPanel({
  view,
  setView,
  ads,
  adsets,
  currency,
  days,
}: {
  view: "ads" | "adsets";
  setView: (v: "ads" | "adsets") => void;
  ads: UseQueryResult<{ ads: AdDetail[] }>;
  adsets: UseQueryResult<{ adsets: AdsetDetail[] }>;
  currency: string;
  days: number;
}) {
  return (
    <>
      <SegmentedControl
        ariaLabel="Breakdown"
        className="mb-2"
        options={[
          { value: "ads", label: "Ads" },
          { value: "adsets", label: "Ad sets" },
        ]}
        value={view}
        onChange={setView}
      />

      {view === "ads" ? (
        ads.isLoading ? (
          <div className="flex items-center gap-2 py-2 text-xs text-muted">
            <Spinner className="h-4 w-4" /> Loading ads…
          </div>
        ) : ads.error ? (
          <ErrorNote message={(ads.error as Error).message} />
        ) : (ads.data?.ads.length ?? 0) === 0 ? (
          <div className="py-2 text-xs text-muted">
            No ad-level data yet — requires the direct Meta adapter + the ads migration; re-sync to
            populate.
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-hairline">
            <TableScroll>
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-hairline text-left uppercase tracking-wide text-muted">
                    <th className="px-3 py-2 font-medium">Ad</th>
                    <th className="px-3 py-2 font-medium">Creative age</th>
                    <th className="px-3 py-2 font-medium">Freq · {days}d</th>
                    <th className="px-3 py-2 text-right font-medium">Spend</th>
                    <th className="px-3 py-2 text-right font-medium">CTR</th>
                    <th className="px-3 py-2 text-right font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {ads.data!.ads.map((a) => (
                    <tr key={a.id} className="border-b border-hairline/50 last:border-0">
                      <td className="px-3 py-2 text-ink-secondary">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span>{a.name}</span>
                          <LifecyclePills lifecycle={a.lifecycle} />
                        </div>
                      </td>
                      <td
                        className={clsx("px-3 py-2 tnum", a.creativeFatigue && "text-critical")}
                        title={a.creativeFatigue ? "Creative Fatigue (over threshold)" : undefined}
                      >
                        {a.creativeAgeDays == null ? "—" : `${a.creativeAgeDays}d`}
                        {a.creativeFatigue && " ⚠"}
                      </td>
                      <td
                        className={clsx("px-3 py-2 tnum", a.highFrequency && "text-critical")}
                        title={a.highFrequency ? "High Frequency (over threshold)" : undefined}
                      >
                        {a.frequency == null ? "—" : a.frequency.toFixed(1)}
                      </td>
                      <td className="px-3 py-2 text-right tnum">{money(a.spend, currency)}</td>
                      <td className="px-3 py-2 text-right tnum">
                        {a.ctr == null ? "—" : `${a.ctr}%`}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <StatusBadge status={a.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>
          </div>
        )
      ) : adsets.isLoading ? (
        <div className="flex items-center gap-2 py-2 text-xs text-muted">
          <Spinner className="h-4 w-4" /> Loading ad sets…
        </div>
      ) : adsets.error ? (
        <ErrorNote message={(adsets.error as Error).message} />
      ) : (adsets.data?.adsets.length ?? 0) === 0 ? (
        <div className="py-2 text-xs text-muted">
          No ad-set data yet — requires the direct Meta adapter; re-sync after cutover.
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-hairline">
          <TableScroll>
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-hairline text-left uppercase tracking-wide text-muted">
                  <th className="px-3 py-2 font-medium">Ad set</th>
                  <th className="px-3 py-2 font-medium">Counting</th>
                  <th className="px-3 py-2 text-right font-medium">Spend</th>
                  <th className="px-3 py-2 text-right font-medium">Conversions</th>
                  <th className="px-3 py-2 text-right font-medium">Cost / conv</th>
                  <th className="px-3 py-2 text-right font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {adsets.data!.adsets.map((a) => (
                  <tr key={a.id} className="border-b border-hairline/50 last:border-0">
                    <td className="px-3 py-2 text-ink-secondary">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span>{a.name}</span>
                        <LifecyclePills lifecycle={a.lifecycle} />
                      </div>
                    </td>
                    <td className="px-3 py-2 text-muted">{conversionLabel(a.conversionType)}</td>
                    <td className="px-3 py-2 text-right tnum">
                      <div>{money(a.current.spend, currency)}</div>
                      <DeltaBadge delta={a.deltas.spend} good="neutral" />
                    </td>
                    <td className="px-3 py-2 text-right tnum">
                      <div>{num(a.current.conversions)}</div>
                      <DeltaBadge delta={a.deltas.conversions} good="up" />
                    </td>
                    <td className="px-3 py-2 text-right tnum">
                      <div>
                        {a.current.costPerConversion == null
                          ? "—"
                          : money(a.current.costPerConversion, currency, 2)}
                      </div>
                      <DeltaBadge delta={a.deltas.costPerConversion} good="down" />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <FlagBadge flag={a.flag} reasons={a.reasons} size="sm" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
        </div>
      )}
    </>
  );
}

/** Desktop table row for one campaign. */
function CampaignRow({
  c,
  isAdmin,
  clientId,
  currency,
  days,
}: {
  c: CampaignDetail;
  isAdmin: boolean;
  clientId: string;
  currency: string;
  days: number;
}) {
  const { open, setOpen, view, setView, override, ads, adsets } = useCampaignRow(c, clientId, days);
  return (
    <>
      <tr className="border-b border-hairline/60 last:border-0 hover:bg-surface-2">
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setOpen((o) => !o)}
              className="text-muted hover:text-ink"
              title={open ? "Hide ads" : "Show ads"}
              aria-label="Toggle ads"
            >
              <span className={clsx("inline-block transition-transform", open && "rotate-90")}>▸</span>
            </button>
            <span className="font-medium text-ink">{c.name}</span>
            <StatusBadge status={c.status} />
          </div>
          <div className="ml-5 text-[10px] uppercase tracking-wide text-muted">{c.objective ?? "—"}</div>
        </td>
        <td className="px-4 py-3">
          {isAdmin ? (
            <select
              className="input py-1 text-xs"
              value={c.conversionTypeOverride ?? ""}
              disabled={override.isPending}
              onChange={(e) => override.mutate(e.target.value || null)}
            >
              <option value="">Auto ({conversionLabel(c.resolvedConversionType)})</option>
              {CONVERSION_TYPES.map((t) => (
                <option key={t} value={t}>
                  {conversionLabel(t)}
                </option>
              ))}
            </select>
          ) : (
            <span className="text-ink-secondary">{conversionLabel(c.conversionType)}</span>
          )}
        </td>
        <td className="px-4 py-3 text-right tnum">
          <div>{money(c.current.spend, currency)}</div>
          <DeltaBadge delta={c.deltas.spend} good="neutral" />
        </td>
        <td className="px-4 py-3 text-right tnum">
          <div>{num(c.current.conversions)}</div>
          <DeltaBadge delta={c.deltas.conversions} good="up" />
        </td>
        <td className="px-4 py-3 text-right tnum">
          <div>{c.current.costPerConversion == null ? "—" : money(c.current.costPerConversion, currency, 2)}</div>
          <DeltaBadge delta={c.deltas.costPerConversion} good="down" />
        </td>
        <td className="px-4 py-3 text-right">
          <div className="flex flex-col items-end gap-1">
            <FlagBadge flag={c.flag} reasons={c.reasons} />
            <CampaignFlagChips c={c} days={days} />
            <DiagnosticPills diagnostics={c.diagnostics} currency={currency} />
            <LifecyclePills lifecycle={c.lifecycle} />
          </div>
        </td>
      </tr>
      {open && (
        <tr className="bg-surface-2/40">
          <td colSpan={6} className="px-4 pb-3 pl-9">
            <CampaignDetailPanel
              view={view}
              setView={setView}
              ads={ads}
              adsets={adsets}
              currency={currency}
              days={days}
            />
          </td>
        </tr>
      )}
    </>
  );
}

/** Mobile card for one campaign — the table row reflowed for narrow screens. */
function CampaignCard({
  c,
  isAdmin,
  clientId,
  currency,
  days,
}: {
  c: CampaignDetail;
  isAdmin: boolean;
  clientId: string;
  currency: string;
  days: number;
}) {
  const { open, setOpen, view, setView, override, ads, adsets } = useCampaignRow(c, clientId, days);
  return (
    <div className="card p-3">
      <button
        className="flex w-full items-start justify-between gap-2 text-left"
        onClick={() => setOpen((o) => !o)}
        aria-label="Toggle ads"
      >
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-ink">{c.name}</span>
            <StatusBadge status={c.status} />
          </div>
          <div className="text-[10px] uppercase tracking-wide text-muted">{c.objective ?? "—"}</div>
        </div>
        <span className={clsx("mt-1 shrink-0 text-muted transition-transform", open && "rotate-90")}>
          ▸
        </span>
      </button>

      <div className="mt-3 grid grid-cols-3 gap-2">
        <MiniMetric
          label="Spend"
          value={money(c.current.spend, currency)}
          delta={c.deltas.spend}
          good="neutral"
        />
        <MiniMetric
          label="Conv."
          value={num(c.current.conversions)}
          delta={c.deltas.conversions}
          good="up"
        />
        <MiniMetric
          label="Cost/conv"
          value={c.current.costPerConversion == null ? "—" : money(c.current.costPerConversion, currency, 2)}
          delta={c.deltas.costPerConversion}
          good="down"
        />
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-1">
        <FlagBadge flag={c.flag} reasons={c.reasons} />
        <CampaignFlagChips c={c} days={days} />
        <DiagnosticPills diagnostics={c.diagnostics} currency={currency} />
        <LifecyclePills lifecycle={c.lifecycle} />
      </div>

      {isAdmin && (
        <div className="mt-2">
          <label className="label">Counting</label>
          <select
            className="input py-1 text-xs"
            value={c.conversionTypeOverride ?? ""}
            disabled={override.isPending}
            onChange={(e) => override.mutate(e.target.value || null)}
          >
            <option value="">Auto ({conversionLabel(c.resolvedConversionType)})</option>
            {CONVERSION_TYPES.map((t) => (
              <option key={t} value={t}>
                {conversionLabel(t)}
              </option>
            ))}
          </select>
        </div>
      )}

      {open && (
        <div className="mt-3 border-t border-hairline pt-3">
          <CampaignDetailPanel
            view={view}
            setView={setView}
            ads={ads}
            adsets={adsets}
            currency={currency}
            days={days}
          />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Change annotations — the hybrid layer over the charts.
// ---------------------------------------------------------------------------

/**
 * Legend + "add" affordance + the detail panel for a clicked marker date.
 * Auto markers are enriched (the "why") in place; manual annotations cover
 * off-platform events. Admins edit; clients see only client-visible markers.
 */
function AnnotationsSection({
  clientId,
  isAdmin,
  annotations,
  campaigns,
  selectedDate,
  onSelectDate,
  days,
}: {
  clientId: string;
  isAdmin: boolean;
  annotations: Annotation[];
  campaigns: CampaignDetail[];
  selectedDate: string | null;
  onSelectDate: (d: string | null) => void;
  days: number;
}) {
  const qc = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [showDismissed, setShowDismissed] = useState(false);
  const byDate = useMemo(() => markersByDate(annotations), [annotations]);
  const selected = selectedDate ? byDate.get(selectedDate) ?? [] : [];
  const typesPresent = useMemo(
    () => [...new Set(annotations.map((a) => a.type))],
    [annotations]
  );
  const unannotated = annotations.filter(isUnannotatedAuto).length;
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["clientDetail", clientId] });
    qc.invalidateQueries({ queryKey: ["annotations", clientId] });
  };

  // Dismissed (hidden) markers — fetched only when the admin toggles them on.
  const dismissedQuery = useQuery({
    queryKey: ["annotations", clientId, days, "dismissed"],
    queryFn: () =>
      api.get<{ annotations: Annotation[] }>(
        `/api/clients/${clientId}/annotations?days=${days}&dismissed=true`
      ),
    enabled: isAdmin && showDismissed,
  });
  const restore = useMutation({
    mutationFn: (id: string) => api.patch(`/api/annotations/${id}`, { dismissed: false }),
    onSuccess: invalidate,
  });

  if (!isAdmin && annotations.length === 0) return null;

  return (
    <div className="mt-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[11px] text-muted">
        <span className="font-medium text-ink-secondary">Change markers</span>
        {typesPresent.map((t) => (
          <span key={t} className="inline-flex items-center gap-1">
            <span
              className="h-2 w-2 rounded-full"
              style={{ background: ANNOTATION_TYPE_META[t].color }}
            />
            {ANNOTATION_TYPE_META[t].label}
          </span>
        ))}
        {annotations.length > 0 && (
          <span className="inline-flex items-center gap-1">
            <span className="h-2 w-2 rounded-full border-2 border-muted bg-transparent" /> unannotated
          </span>
        )}
        {isAdmin && unannotated > 0 && (
          <span className="text-warning">{unannotated} need a why</span>
        )}
        {annotations.length === 0 && (
          <span className="text-muted">No changes flagged in this range.</span>
        )}
        {isAdmin && (
          <div className="ml-auto flex items-center gap-2">
            <button
              className="btn-ghost px-2 py-0.5 text-xs"
              onClick={() => setShowDismissed((v) => !v)}
            >
              {showDismissed ? "Hide dismissed" : "Show dismissed"}
            </button>
            <button className="btn-ghost px-2 py-0.5 text-xs" onClick={() => setShowAdd(true)}>
              + Add annotation
            </button>
          </div>
        )}
      </div>

      {isAdmin && showDismissed && (
        <Card className="mt-3">
          <div className="mb-2 flex items-center justify-between">
            <h4 className="text-sm font-semibold text-ink">
              Dismissed markers{" "}
              <span className="font-normal text-muted">· hidden from the charts</span>
            </h4>
            <button
              className="text-xs text-muted hover:text-ink"
              onClick={() => setShowDismissed(false)}
              aria-label="Close"
            >
              ✕
            </button>
          </div>
          {dismissedQuery.isLoading ? (
            <div className="flex items-center gap-2 py-1 text-xs text-muted">
              <Spinner className="h-4 w-4" /> Loading…
            </div>
          ) : (dismissedQuery.data?.annotations.length ?? 0) === 0 ? (
            <p className="text-sm text-muted">No dismissed markers in this range.</p>
          ) : (
            <ul className="space-y-1.5">
              {dismissedQuery.data!.annotations.map((a) => (
                <li
                  key={a.id}
                  className="flex items-center gap-2 rounded-lg border border-hairline bg-surface-2 px-3 py-2 text-xs"
                >
                  <span
                    className="h-2 w-2 shrink-0 rounded-full"
                    style={{ background: ANNOTATION_TYPE_META[a.type].color }}
                  />
                  <span className="tnum shrink-0 text-ink-secondary">{shortDate(a.date)}</span>
                  <span className="truncate text-muted">
                    {a.autoSummary ?? a.note ?? "Change"}
                  </span>
                  <button
                    className="btn-ghost ml-auto shrink-0 px-2 py-0.5"
                    disabled={restore.isPending}
                    onClick={() => restore.mutate(a.id)}
                  >
                    Restore
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      {selected.length > 0 && selectedDate && (
        <Card className="mt-3">
          <div className="mb-3 flex items-center justify-between">
            <h4 className="text-sm font-semibold text-ink">
              {longDate(selectedDate)}{" "}
              <span className="font-normal text-muted">
                · {selected.length} change{selected.length > 1 ? "s" : ""}
              </span>
            </h4>
            <button
              className="text-xs text-muted hover:text-ink"
              onClick={() => onSelectDate(null)}
              aria-label="Close"
            >
              ✕
            </button>
          </div>
          <div className="space-y-3">
            {selected.map((a) => (
              <AnnotationCard key={a.id} a={a} isAdmin={isAdmin} onChanged={invalidate} />
            ))}
          </div>
        </Card>
      )}

      {showAdd && (
        <AddAnnotationModal
          clientId={clientId}
          campaigns={campaigns}
          onClose={() => setShowAdd(false)}
          onSaved={() => {
            setShowAdd(false);
            invalidate();
          }}
        />
      )}
    </div>
  );
}

/** One marker in the detail panel. Admins get inline edit + the "why" field. */
function AnnotationCard({
  a,
  isAdmin,
  onChanged,
}: {
  a: Annotation;
  isAdmin: boolean;
  onChanged: () => void;
}) {
  const [note, setNote] = useState(a.note ?? "");
  const [type, setType] = useState<AnnotationType>(a.type);
  const [expanded, setExpanded] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (patch: Record<string, unknown>) => api.patch(`/api/annotations/${a.id}`, patch),
    onSuccess: onChanged,
    onError: (e) => setErr((e as Error).message),
  });
  const remove = useMutation({
    mutationFn: () => api.del(`/api/annotations/${a.id}`),
    onSuccess: onChanged,
    onError: (e) => setErr((e as Error).message),
  });

  const meta = ANNOTATION_TYPE_META[a.type];
  const needWhy = isUnannotatedAuto(a);

  return (
    <div className="rounded-lg border border-hairline bg-surface-2 p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span
            className="rounded-full px-2 py-0.5 font-medium"
            style={{ background: `${meta.color}22`, color: meta.color }}
          >
            {meta.label}
          </span>
          <span className="text-muted">
            {a.scope === "campaign" ? a.campaignName ?? "Campaign" : "Account"}
          </span>
          <span className="rounded bg-surface-3 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted">
            {a.source === "meta_auto" ? "Auto" : "Manual"}
          </span>
          {needWhy && <span className="text-[10px] text-warning">needs a why</span>}
        </div>
        {isAdmin && (
          <button
            className="shrink-0 text-xs text-muted hover:text-critical disabled:opacity-50"
            onClick={() => remove.mutate()}
            disabled={remove.isPending}
            title={
              a.source === "meta_auto"
                ? "Dismiss — won't re-appear on the next sync"
                : "Delete permanently"
            }
          >
            {a.source === "meta_auto" ? "Dismiss" : "Delete"}
          </button>
        )}
      </div>

      {a.autoSummary && <p className="mt-1.5 text-sm text-ink-secondary">{a.autoSummary}</p>}

      {a.changes && a.changes.length > 0 && (
        <div className="mt-1">
          <button
            className="text-[11px] text-muted hover:text-ink"
            onClick={() => setExpanded((e) => !e)}
          >
            {expanded ? "Hide" : "Show"} {a.changes.length} underlying change
            {a.changes.length > 1 ? "s" : ""}
          </button>
          {expanded && (
            <ul className="mt-1 space-y-0.5 border-l border-hairline pl-3 text-[11px] text-muted">
              {a.changes.map((c) => (
                <li key={c.ref}>
                  <span className="tnum">{dateTime(c.time)}</span> — {c.summary}
                  {c.actor ? ` · ${c.actor}` : ""}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {isAdmin ? (
        <div className="mt-2 space-y-2">
          <textarea
            className="input text-sm"
            rows={2}
            placeholder={a.source === "meta_auto" ? "Add the why…" : "Note"}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <div className="flex flex-wrap items-center gap-2">
            <select
              className="input max-w-[9rem] py-1 text-xs"
              value={type}
              onChange={(e) => setType(e.target.value as AnnotationType)}
            >
              {ANNOTATION_TYPES.map((t) => (
                <option key={t} value={t}>
                  {ANNOTATION_TYPE_META[t].label}
                </option>
              ))}
            </select>
            <label className="flex items-center gap-1.5 text-xs text-muted">
              <input
                type="checkbox"
                checked={a.visibleToClient}
                disabled={save.isPending}
                onChange={(e) => save.mutate({ visible_to_client: e.target.checked })}
              />
              Client-visible
            </label>
            <button
              className="btn-primary ml-auto px-2.5 py-1 text-xs"
              disabled={save.isPending}
              onClick={() => save.mutate({ note: note.trim() === "" ? null : note.trim(), type })}
            >
              {save.isPending && <Spinner className="h-3 w-3" />} Save
            </button>
          </div>
          {err && <ErrorNote message={err} />}
        </div>
      ) : (
        a.note && <p className="mt-2 text-sm text-ink">{a.note}</p>
      )}
    </div>
  );
}

/** Admin form for a manual annotation (off-platform events + context). */
function AddAnnotationModal({
  clientId,
  campaigns,
  onClose,
  onSaved,
}: {
  clientId: string;
  campaigns: CampaignDetail[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [scope, setScope] = useState<"client" | "campaign">("client");
  const [campaignId, setCampaignId] = useState("");
  const [type, setType] = useState<AnnotationType>("external");
  const [note, setNote] = useState("");
  const [visible, setVisible] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      api.post(`/api/clients/${clientId}/annotations`, {
        date,
        scope,
        campaign_id: scope === "campaign" ? campaignId || null : null,
        type,
        note,
        visible_to_client: visible,
      }),
    onSuccess: onSaved,
    onError: (e) => setErr((e as Error).message),
  });

  return (
    <Modal open onClose={onClose} title="Add annotation">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErr(null);
          save.mutate();
        }}
        className="space-y-4"
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="label">Date</label>
            <input
              type="date"
              className="input tnum"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="label">Type</label>
            <select
              className="input"
              value={type}
              onChange={(e) => setType(e.target.value as AnnotationType)}
            >
              {ANNOTATION_TYPES.map((t) => (
                <option key={t} value={t}>
                  {ANNOTATION_TYPE_META[t].label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="label">Applies to</label>
          <select
            className="input"
            value={scope}
            onChange={(e) => setScope(e.target.value as "client" | "campaign")}
          >
            <option value="client">Whole account</option>
            <option value="campaign">A specific campaign</option>
          </select>
          {scope === "campaign" && (
            <select
              className="input mt-2"
              value={campaignId}
              onChange={(e) => setCampaignId(e.target.value)}
              required
            >
              <option value="">— choose a campaign —</option>
              {campaigns.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          )}
        </div>

        <div>
          <label className="label">Note (the why)</label>
          <textarea
            className="input"
            rows={3}
            placeholder="e.g. Stockout on the hero product; site down 2h; new price live."
            value={note}
            onChange={(e) => setNote(e.target.value)}
            required
          />
        </div>

        <label className="flex items-center gap-2 text-sm text-ink-secondary">
          <input type="checkbox" checked={visible} onChange={(e) => setVisible(e.target.checked)} />
          Visible to the client
        </label>

        {err && <ErrorNote message={err} />}

        <div className="flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={save.isPending}>
            {save.isPending && <Spinner className="h-4 w-4" />}
            Add annotation
          </button>
        </div>
      </form>
    </Modal>
  );
}
